import React, { useEffect, useMemo, useState, useCallback } from "react";
import { NavLink,Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useNotification } from "../context/NotificationContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTachometerAlt,
  faUsers,
  faShieldAlt,
  faFileInvoiceDollar,
  faDownload,
  faSignOutAlt,
  faBars,
  faBuilding,
  faListAlt,
  faHistory,
  faExchangeAlt,
  faCalendarAlt,
  faClock,
  faPlus,
  // NEW: caret icons
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { showError, showSuccess } from "../utils/toast";
import ProfileEditModal from "./ProfileEditModal"; // Import the modal component

/**
 * Controlled Sidebar
 * - Mobile (<= 992px): drawer; visibility controlled by props.isOpen
 * - Desktop: can be collapsed locally (80px) or expanded (250px)
 */
const Sidebar = ({ isOpen, toggleSidebar }) => {
  const { user } = useAuth();
  const { notificationsCount, fetchNotificationCount } = useNotification();

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 992 : false
  );
  const [collapsed, setCollapsed] = useState(
    () => JSON.parse(localStorage.getItem("sidebarCollapsed") || "false")
  );
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false); // Add this line

  const agent = user || JSON.parse(localStorage.getItem("user") || "null");
  const selectedCategory =
    localStorage.getItem("selectedInsuranceType") || "Life Insurance";

  // keep mobile flag in sync with resize
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 992);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // fetch notifications once agent known
  useEffect(() => {
    if (agent?.id) fetchNotificationCount();
  }, [agent?.id, fetchNotificationCount]);

  // prevent body scroll when mobile drawer is open
  useEffect(() => {
    if (isMobile && isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [isMobile, isOpen]);

  const premiumDueCount = 0;

  const generalInsuranceMenu = useMemo(
    () => [
      { label: "Dashboard", path: "/agent/dashboard", icon: faTachometerAlt },
      { label: "Import Policy", path: "/agent/import-excel", icon: faFileInvoiceDollar },
      { label: "Add new Policy", path: "/agent/add-manual", icon: faListAlt },
      { label: "Company", path: "/agent/companies", icon: faBuilding },
      { label: "Customer List", path: "/agent/customers", icon: faUsers },
      { label: "Policy Alterations", path: "/agent/policy-alterations", icon: faExchangeAlt },
       { label: "Premium Due", path: "/agent/due-payments", icon: faCalendarAlt, badge: premiumDueCount },
      { label: "Notifications", path: "/agent/notification", icon: faPlus, badge: notificationsCount || 0 },
      { label: "Logout", path: "/logout", icon: faSignOutAlt },
    ],
    [notificationsCount]
  );

  const lifeInsuranceMenu = useMemo(
    () => [
      { label: "Dashboard", path: "/agent/dashboard", icon: faTachometerAlt },
      { label: "Import Policy", path: "/agent/import-excel", icon: faFileInvoiceDollar },
      { label: "Add New Policy", path: "/agent/add-manual", icon: faListAlt },
      {
        label: "Family",
        icon: faShieldAlt,
        children: [
          { label: "Add Family", path: "/agent/add-family", icon: faUsers },
          { label: "Family History", path: "/agent/family-history", icon: faHistory },
        ],
      },
      { label: "Company", path: "/agent/companies", icon: faBuilding },
      { label: "Customer List", path: "/agent/customers", icon: faUsers },
      { label: "Premium Due", path: "/agent/due-payments", icon: faCalendarAlt, badge: premiumDueCount },
      { label: "Policy Alterations", path: "/agent/policy-alterations", icon: faExchangeAlt },
      { label: "Notifications", path: "/agent/notification", icon: faPlus, badge: notificationsCount || 0 },
      // { label: "Premium Calculator", path: "/agent/premium-calculater", icon: faClock },
      { label: "Logout", path: "/logout", icon: faSignOutAlt },
    ],
    [notificationsCount]  
  );

  const customerMenu = [
    { label: "Dashboard", path: "/customer/dashboard", icon: faTachometerAlt },
    { label: "Add Policies", path: "/customer/addinsurance", icon: faDownload },
    { label: "Policies", path: "/customer/mypolicies", icon: faShieldAlt },
    { label: "Due Payments", path: "/customer/due-payments", icon: faFileInvoiceDollar },
    { label: "Logout", path: "/logout", icon: faSignOutAlt },
  ];

  const menu =
    user?.role === "Agent"
      ? selectedCategory === "General Insurance"
        ? generalInsuranceMenu
        : lifeInsuranceMenu
      : customerMenu;

  const onHeaderToggle = () => {
    if (isMobile) {
      toggleSidebar?.();
    } else {
      setCollapsed((prev) => {
        localStorage.setItem("sidebarCollapsed", JSON.stringify(!prev));
        return !prev;
      });
    }
  };
  
  const handleLogout = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("user"); // if you save user info
  showSuccess("Logged Out successfully")
  window.location.href = "/login";
};

  const handleDropdown = (label) =>
    setOpenDropdown((cur) => (cur === label ? null : label));

  // close drawer after clicking a link (mobile only)
  const handleLinkClick = useCallback(() => {
    if (isMobile && isOpen) toggleSidebar?.();
  }, [isMobile, isOpen, toggleSidebar]);

  //for getting the intial of the world
const getInitials = (name = "") => {
  if (!name) return "?";

  const parts = name.trim().split(" ").filter(Boolean);

  // Case 1: First + Last name available
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  // Case 2: Only first name → return first 2 letters
  return parts[0].substring(0, 2).toUpperCase();
};

  // ==== helpers (same content for mobile/desktop to avoid duplication bugs) ====
  const renderMenuList = () => (
    <ul>
      {menu.map((item, i) =>
        item.children ? (
          <li key={i}>
            <button
              className={`menu-item has-children ${openDropdown === item.label ? "open" : ""}`}
              onClick={() => handleDropdown(item.label)}
              aria-expanded={openDropdown === item.label}
              aria-controls={`submenu-${i}`}
            >
              <div className="menu-start">
                <FontAwesomeIcon icon={item.icon} className="menu-icon" />
                {!collapsed && <span className="menu-text">{item.label}</span>}
              </div>

              {/* caret on the right (hidden when collapsed) */}
              {!collapsed && (
                <FontAwesomeIcon
                  icon={openDropdown === item.label ? faChevronDown : faChevronRight}
                  className="menu-caret"
                />
              )}
            </button>

            {!collapsed && openDropdown === item.label && (
              <ul id={`submenu-${i}`} className="submenu">
                {item.children.map((child, j) => (
                  <li key={`${i}-${j}`}>
                    <NavLink
                      to={child.path}
                      className={({ isActive }) => (isActive ? "active" : "")}
                      onClick={handleLinkClick}
                    >
                      <FontAwesomeIcon icon={child.icon} className="menu-icon" />
                      <span className="menu-text">{child.label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            )}
          </li>
        )  : (
        <li key={i}>
          {item.label === "Logout" ? (
            <button
              className="menu-item"
              onClick={handleLogout}
            >
              <FontAwesomeIcon icon={item.icon} className="menu-icon" />
              {!collapsed && <span className="menu-text">{item.label}</span>}
            </button>
          ) : (
            <NavLink
              to={item.path}
              className={({ isActive }) => (isActive ? "menu-item active" : "menu-item")}
              onClick={handleLinkClick}
              end
            >
              <FontAwesomeIcon icon={item.icon} className="menu-icon" />
              {!collapsed && <span className="menu-text">{item.label}</span>}
              {!!item.badge && !collapsed && <span className="menu-badge">{item.badge}</span>}
            </NavLink>
          )}
        </li>
      )
    )}
  </ul>
);
  const renderFooter = () => (
    <>
      <div 
        className="sidebar__footer" 
        onClick={() => setShowProfileModal(true)}
        style={{ cursor: 'pointer' }}
      >
        <div  className="avatar bg-primary text-center p-2" >
                    {getInitials(user?.name)}
                  </div>
        {!collapsed && (
          <div className="foot-text">
            <div className="name">{user?.name || "User"}</div>
            <div className="role">Admin Panel</div>
          </div>
        )}
      </div>

      {/* Profile Modal */}
      <ProfileEditModal 
        show={showProfileModal}
        onHide={() => setShowProfileModal(false)}
        user={user}
      />
    </>
  );

  return (
    <>
      {/* Mobile topbar (logo + toggle) */}
      {isMobile && (
        <div className="mobile-topbar">
          <div className="mobile-topbar__brand">Insurance</div>
          <button
            type="button"
            className="mobile-topbar__toggle"
            aria-label="Open menu"
            onClick={toggleSidebar}
          >
            <FontAwesomeIcon icon={faBars} />
          </button>
        </div>
      )}

      {/* Mobile overlay */}
      {isMobile && isOpen && <div className="sidebar-overlay" onClick={toggleSidebar} />}

      {/* Only ONE sidebar exists at a time */}
      {isMobile ? (
        <aside
          className={[
            "sidebar",
            isOpen ? "drawer-open" : "drawer-closed", // mobile only
          ].join(" ")}
        >
          {/* keep header for consistency (toggle does the same) */}
          <div className="sidebar__header">
            <button className="icon-btn" onClick={onHeaderToggle} aria-label="Toggle sidebar">
              <FontAwesomeIcon icon={faBars} />
            </button>
          </div>

          <nav className="sidebar__menu">{renderMenuList()}</nav>
          {renderFooter()}
        </aside>
      ) : (
        <aside
          className={[
            "sidebar",
            collapsed ? "collapsed" : "expanded", // desktop only
          ].join(" ")}
        >
          <div className="sidebar__header">
            <button className="icon-btn" onClick={onHeaderToggle} aria-label="Toggle sidebar">
              <FontAwesomeIcon icon={faBars} />
            </button>
            {!collapsed && <h3 className="brand">Insurance</h3>}
          </div>

          <nav className="sidebar__menu">{renderMenuList()}</nav>
          {renderFooter()}
        </aside>
      )}
    </>
  );
};

export default Sidebar;
